"use client";

/**
 * The task screen's run controls, in one place for every composer that sits on
 * it: the task layout's box (one-composer-everywhere off) and the Dock's pill
 * (on). Moved out of task/layout.tsx verbatim.
 *
 * D11: background helpers outlive the turn that spawned them, so Stop has two
 * halves: it interrupts the MAIN turn (only if one is running) and, if helpers
 * are still live, it ASKS about them rather than deciding. §3.7: the run
 * controls belong to whatever the column is showing, so with a teammate's
 * screen in view Stop stops that LANE, never Bash's turn behind it.
 */
import { useCallback } from "react";
import type { TaskDoorControl } from "@bash/shared";
import { useTaskStore } from "@/stores/taskStore";
import { useComputerStore } from "@/stores/computerStore";
import {
  useIsProcessing,
  useLaneState,
  useLiveHelperCount,
  useTaskIsProcessing,
} from "@/stores/selectors";
import { useLaneInView } from "@/stores/teamViewStore";
import { useTaskStopPromptStore } from "@/stores/taskStopPromptStore";
import {
  computerOpenTabForTask,
  openComputerForTask,
} from "@/lib/open-computer-for-task";
import { wakeBrowserIfAsleep } from "@/lib/open-browser-for-task";

export interface TaskScreenControls {
  /** A turn is in flight on what the column shows: the lane in view's, else the main turn's. */
  inFlight: boolean;
  /** The one Stop this screen has (R-L6). */
  stopInView: () => void;
  /** The task-screen door's controls (R-U4). False = not handled: the words ride the task (R-L22). */
  onDoorControl: (control: TaskDoorControl, taskId: string) => boolean;
}

/** The task layout's: the store's selection, read exactly as it always was. */
export function useTaskScreenControls(): TaskScreenControls {
  const selectedTaskId = useTaskStore((s) => s.selectedTaskId);
  return useControlsOf(selectedTaskId, useIsProcessing());
}

/**
 * The Dock's pill's: the controls FOR a task it names. The pill is mounted on
 * every route and a selection outlives the page it was picked on, so it hands
 * over its own task in view, and null off a task's screen: every read then
 * answers its inert value, and a task the person has left never re-renders the
 * composer they are typing in (review-task-13 finding 1). Two hooks over one
 * core, never one hook with an optional argument: which selector is read would
 * then hang on the argument, a conditional hook call.
 */
export function useTaskScreenControlsFor(
  taskId: string | null
): TaskScreenControls {
  return useControlsOf(taskId, useTaskIsProcessing(taskId));
}

function useControlsOf(
  selectedTaskId: string | null,
  isProcessing: boolean
): TaskScreenControls {
  const cancelQuery = useTaskStore((s) => s.cancelQuery);
  const cancelTask = useTaskStore((s) => s.cancelTask);
  const liveHelpers = useLiveHelperCount(selectedTaskId);
  const laneInView = useLaneInView(selectedTaskId);
  const laneComposer = useLaneState(selectedTaskId, laneInView);

  // The Stop BUTTON always stops (no grace). The callback re-binds with the
  // turn and the helper count on purpose: what Stop MEANS depends on both.
  const cancelFromButton = useCallback(() => {
    if (isProcessing) cancelQuery("button");
    if (liveHelpers > 0)
      useTaskStopPromptStore.getState().raise(selectedTaskId);
  }, [cancelQuery, isProcessing, liveHelpers, selectedTaskId]);
  // 'helper' is the source the runner reads (R85): it is what lets a lane
  // PAUSED on a question of its own still be stopped.
  const cancelLane = useCallback(() => {
    if (laneComposer) cancelTask(laneComposer.teammateTaskId, "helper");
  }, [cancelTask, laneComposer]);
  const stopInView = laneComposer ? cancelLane : cancelFromButton;

  // The task-screen door (spec 2026-09-20 §3.2, R-U4): a control the server
  // named runs the very handler the screen's own controls run, never a copy.
  // The store is read through getState so a control never closes over a stale
  // one, and the task id is the one the route was asked about.
  const onDoorControl = useCallback(
    (control: TaskDoorControl, taskId: string): boolean => {
      switch (control) {
        case "open_computer": {
          // Asked for, not clicked for: the Line's spoken "open the computer"
          // arrives as this control, and nobody's hand is on the page to click
          // a sleeping pane awake. So an open that lands on Chrome wakes the
          // box through the story's ONE guarded wake (a live pane, or a wake
          // in flight, is left alone). Files needs no live browser. The
          // landing is read at ask time, like the rest of this rule.
          const landsOnBrowser = computerOpenTabForTask(taskId) === "browser";
          openComputerForTask(taskId);
          if (landsOnBrowser) wakeBrowserIfAsleep();
          return true;
        }
        case "close_computer":
          useComputerStore.getState().close(taskId);
          return true;
        case "stop":
          // R-L22: with nothing in flight a typed stop is not a control (the
          // Stop key is not even offered then), so it is not handled and the
          // words ride the task as a message.
          if (!(laneComposer ? laneComposer.live : isProcessing)) return false;
          stopInView();
          return true;
        default: {
          // A control this bundle does not know does nothing, and a new member
          // of TaskDoorControl fails the build here rather than riding Stop.
          const never: never = control;
          return never;
        }
      }
    },
    [isProcessing, laneComposer, stopInView]
  );

  return {
    inFlight: laneComposer ? laneComposer.live : isProcessing,
    stopInView,
    onDoorControl,
  };
}
